import { useEffect, useRef, useState } from "react";

interface UseInViewOptions {
  /** Fração do elemento visível para disparar (0–1). */
  threshold?: number;
  /** Margem do root — negativo em baixo adia o disparo até o elemento subir um pouco. */
  rootMargin?: string;
  /** Se true, dispara uma única vez e para de observar. */
  once?: boolean;
}

/**
 * Observa quando um elemento entra na viewport (IntersectionObserver).
 * Respeita `prefers-reduced-motion` e navegadores sem suporte, revelando
 * o conteúdo imediatamente nesses casos.
 */
export function useInView<T extends HTMLElement = HTMLDivElement>({
  threshold = 0.15,
  rootMargin = "0px 0px -10% 0px",
  once = true,
}: UseInViewOptions = {}) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (typeof IntersectionObserver === "undefined" || reduceMotion) {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setInView(false);
        }
      },
      { threshold, rootMargin },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin, once]);

  return { ref, inView };
}
